import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import { createPortal } from "react-dom";
import {
  useReactTable,
  getCoreRowModel,
  getGroupedRowModel,
  getExpandedRowModel,
  getSortedRowModel,
  flexRender,
  type SortingState,
  type ExpandedState,
  type RowSelectionState,
  type Header,
  type ColumnOrderState,
  type CellContext,
  type ColumnDef,
} from "@tanstack/react-table";
import { useVirtualizer, measureElement as virtualMeasureElement } from "@tanstack/react-virtual";
import type { TanTableRenderModel } from "../lib/tanTableSpec.js";
import type { TanTableRowSelectEvent } from "../lib/pageFilters.js";
import {
  buildColumnDefs,
  resolveGrouping,
  getFoldControlFields,
  buildInitialSorting,
  buildColumnPinning,
  buildInitialExpanded,
  computeGrandTotal,
  buildColumnFormatters,
  evaluateConditionalFormat,
  type TanTableColumnMeta,
} from "../lib/tanTableEngine.js";
import { buildTanTableTsv } from "../lib/tanTableExport.js";
import { apiFetchBlob, triggerBlobDownload } from "../lib/api.js";
import { useToast } from "./Toast.js";
import { useConfirm } from "./ConfirmDialog.js";

/** Soft warning above this row count: copy works but may briefly freeze the browser. */
const COPY_SOFT_LIMIT_ROWS = 50_000;
/** Hard cap above this row count: button disabled, user must Export. Clipboard payloads at this scale risk silent truncation across browsers. */
const COPY_HARD_LIMIT_ROWS = 250_000;

async function writeTextToClipboard(text: string): Promise<void> {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.style.position = "fixed";
  ta.style.opacity = "0";
  document.body.appendChild(ta);
  ta.select();
  try {
    document.execCommand("copy");
  } finally {
    document.body.removeChild(ta);
  }
}

type RowArray = unknown[];

type TanRenderMarker = { __tanRender: string; text: string; href?: string; color?: string };

/** Cell renderers may return plain objects for link/badge; React cannot render those — convert to elements. */
function unwrapTanTableCellRender(value: unknown): ReactNode {
  if (value == null) return null;
  if (typeof value === "object" && !Array.isArray(value) && "__tanRender" in value) {
    const v = value as TanRenderMarker;
    if (v.__tanRender === "link") {
      return (
        <a href={v.href} className="tanTableCellLink" target="_blank" rel="noopener noreferrer">
          {v.text}
        </a>
      );
    }
    if (v.__tanRender === "badge") {
      return (
        <span className="tanTableCellBadge" style={v.color ? { backgroundColor: v.color } : undefined}>
          {v.text}
        </span>
      );
    }
    return <span className="tanTableCellBadge">{String(v.text ?? "")}</span>;
  }
  return value as ReactNode;
}

/**
 * TanStack's flexRender uses createElement(Comp, props) when Comp is a function, so the cell runs as a
 * component and returning { __tanRender } from it hits "Objects are not valid as a React child".
 * Call function cells directly, then unwrap marker objects.
 */
function renderColumnCell(
  cellDef: ColumnDef<RowArray, unknown>["cell"] | undefined,
  ctx: CellContext<RowArray, unknown>
): ReactNode {
  if (cellDef == null) return null;
  if (typeof cellDef === "function") {
    return unwrapTanTableCellRender((cellDef as (c: CellContext<RowArray, unknown>) => unknown)(ctx));
  }
  return unwrapTanTableCellRender(flexRender(cellDef, ctx));
}

export type TanTableCustomCells = Partial<
  Record<string, (info: CellContext<RowArray, unknown>) => ReactNode>
>;

export function TanTableViz(props: {
  model: TanTableRenderModel;
  customCells?: TanTableCustomCells;
  showExport?: boolean;
  /** Page builder / live page: syncs TanStack row selection to page filter state. */
  onRowSelect?: (event: TanTableRowSelectEvent) => void;
  /** Multi-row only: when true, bound widgets filter by unselected main-table values (complement). */
  rowFilterUnselected?: boolean;
  onRowFilterUnselectedChange?: (active: boolean) => void;
  /** When true, only selected rows are shown in this table (selection ids stay aligned to full filtered data). */
  isolateSelection?: boolean;
  onIsolateSelectionChange?: (on: boolean) => void;
  /** Show a non-data leftmost row-number column (1..N over currently visible non-group rows). Screen-only — not in copy/export. */
  showRowNumbers?: boolean;
  /**
   * Optional display label for the per-table Excel export. Used as the
   * workbook tab name + suggested filename. Defaults to "Table" when omitted.
   * Pages pass `widget.title_override || item.name`; inspectors pass the
   * node label.
   */
  title?: string;
  /**
   * ADR-0120 §5.6: node reference for the server-side full-data export. When
   * the model carries `render_truncated` (rows capped to fit the size guard),
   * Export POSTs the node-ref workbook route (full data, server-side) instead
   * of the client-posted (truncated) model. Pages pass the widget source.
   */
  exportSourceRef?: { operationId: string; nodeId: string };
}) {
  const {
    model,
    customCells,
    showExport = true,
    onRowSelect,
    rowFilterUnselected = false,
    onRowFilterUnselectedChange,
    isolateSelection = false,
    onIsolateSelectionChange,
    showRowNumbers = false,
  } = props;
  const spec = model.tableSpec;

  const foldFields = useMemo(() => getFoldControlFields(model), [model]);
  const hasFoldControl = foldFields.length > 0;
  const [activeFoldFields, setActiveFoldFields] = useState<Set<string>>(
    () => new Set(foldFields.map((f) => f.field)),
  );

  const columnDefs = useMemo(() => {
    const base = buildColumnDefs(model);
    if (!customCells) return base;
    const patch = (def: ColumnDef<RowArray, unknown>): ColumnDef<RowArray, unknown> => {
      const id = def.id as string | undefined;
      if (id && customCells[id]) {
        return { ...def, cell: customCells[id] };
      }
      if ("columns" in def && Array.isArray(def.columns)) {
        return { ...def, columns: def.columns.map(patch) };
      }
      return def;
    };
    return base.map(patch);
  }, [model, customCells]);
  const activeGrouping = useMemo(
    () => foldFields.filter((f) => activeFoldFields.has(f.field)).map((f) => f.field),
    [foldFields, activeFoldFields],
  );
  const grouping = useMemo(() => resolveGrouping(model, activeGrouping), [model, activeGrouping]);
  const columnFormatters = useMemo(() => buildColumnFormatters(model), [model]);
  const initialPinning = useMemo(() => buildColumnPinning(model), [model]);

  const [sorting, setSorting] = useState<SortingState>(() => buildInitialSorting(model));
  const [expanded, setExpanded] = useState<ExpandedState>(() => buildInitialExpanded(model));
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const skipNextEmptyRowSyncRef = useRef(true);
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>([]);
  const [columnSizing, setColumnSizing] = useState({});
  /** Session-only: resets on unmount. Default shows full wrapped cell text. */
  const [fitContent, setFitContent] = useState(true);

  /** Applied directly to the inner cell div when fit is on; display:block makes it a real box. */
  const fitCellInnerStyle = useMemo<CSSProperties>(
    () => ({ display: "block", width: "100%", minWidth: 0, boxSizing: "border-box" }),
    [],
  );

  const [exporting, setExporting] = useState(false);
  const [copying, setCopying] = useState(false);
  const toast = useToast();
  const confirm = useConfirm();

  const exportTitle = props.title?.trim() || "Table";
  const exportSourceRef = props.exportSourceRef;
  const isTruncated = model.render_truncated?.dropped === "rows";
  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      // ADR-0120 §5.6: a truncated model must not be the export source — the
      // node-ref route materializes the FULL cached data server-side.
      // ADR-0099 Option B otherwise: hub builds the workbook from the table
      // model we send in the request body.
      const { blob, filename, contentType } =
        isTruncated && exportSourceRef
          ? await apiFetchBlob(
              `/api/tenant/operations/${encodeURIComponent(exportSourceRef.operationId)}/nodes/${encodeURIComponent(exportSourceRef.nodeId)}/export-input-workbook`,
              { body: { title: exportTitle } },
            )
          : await apiFetchBlob(
              "/api/tenant/export-table-workbook",
              { body: { table_render_model: model, title: exportTitle } },
            );
      triggerBlobDownload(
        new Blob([blob], { type: contentType }),
        filename ?? `${exportTitle}.xlsx`,
      );
    } catch (e) {
      toast.push({ kind: "error", message: `Export failed: ${e instanceof Error ? e.message : String(e)}` });
    } finally {
      setExporting(false);
    }
  }, [model, exportTitle, toast, isTruncated, exportSourceRef]);

  const handleCopy = useCallback(async () => {
    const rowCount = model.rows.length;
    if (rowCount > COPY_HARD_LIMIT_ROWS) return;
    if (rowCount > COPY_SOFT_LIMIT_ROWS) {
      const ok = await confirm.confirm({
        title: `Copy ${rowCount.toLocaleString()} rows to clipboard?`,
        detail:
          "Tables this large can take a few seconds to copy and may briefly freeze the browser. Excel export is faster for big tables.",
        confirmLabel: "Copy anyway",
      });
      if (!ok) return;
    }
    setCopying(true);
    try {
      const grandTotal = computeGrandTotal(model, model.rows);
      const tsv = buildTanTableTsv(model, model.rows, grandTotal);
      await writeTextToClipboard(tsv);
      toast.push({ kind: "success", message: "Table copied — paste into Excel." });
    } catch (e) {
      toast.push({
        kind: "error",
        message: e instanceof Error ? e.message : "Copy failed",
      });
    } finally {
      setCopying(false);
    }
  }, [model, confirm, toast]);

  const [selectedValues, setSelectedValues] = useState<Record<string, string[]>>({});
  const [openFilterKey, setOpenFilterKey] = useState<string | null>(null);
  const [filterSearchByColumn, setFilterSearchByColumn] = useState<Record<string, string>>({});
  const [draftValuesByColumn, setDraftValuesByColumn] = useState<Record<string, string[]>>({});
  const [addToListByColumn, setAddToListByColumn] = useState<Record<string, boolean>>({});
  const [filterPosition, setFilterPosition] = useState<{ top: number; left: number; ready: boolean } | null>(null);
  const headerCellRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const filterMenuRef = useRef<HTMLDivElement | null>(null);

  const columnIndexByKey = useMemo(
    () => new Map(model.columns.map((c, i) => [c.key, i])),
    [model.columns],
  );

  const filteredRows = useMemo(() => {
    return model.rows.filter((row) =>
      Object.entries(selectedValues).every(([colKey, vals]) => {
        if (vals.length === 0) return true;
        const idx = columnIndexByKey.get(colKey);
        if (idx == null) return true;
        return vals.includes(String(row[idx] ?? ""));
      }),
    );
  }, [model.rows, selectedValues, columnIndexByKey]);

  const grandTotalRow = useMemo(() => computeGrandTotal(model, filteredRows), [model, filteredRows]);

  const selectedSourceIndices = useMemo(
    () =>
      Object.keys(rowSelection)
        .filter((k) => rowSelection[k])
        .map(Number)
        .filter((i) => Number.isFinite(i) && i >= 0 && i < filteredRows.length)
        .sort((a, b) => a - b),
    [rowSelection, filteredRows],
  );

  const tableData = useMemo(() => {
    if (!isolateSelection) return filteredRows;
    if (selectedSourceIndices.length === 0) return [];
    return selectedSourceIndices.map((i) => filteredRows[i]!);
  }, [isolateSelection, filteredRows, selectedSourceIndices]);

  const getRowId = useCallback(
    (_row: RowArray, index: number) => {
      if (isolateSelection && selectedSourceIndices.length > 0) {
        return String(selectedSourceIndices[index]!);
      }
      return String(index);
    },
    [isolateSelection, selectedSourceIndices],
  );

  const hasActiveFilters = Object.values(selectedValues).some((v) => v.length > 0);

  function currentSelectionSet(colKey: string, universe: string[]): Set<string> {
    const raw = selectedValues[colKey] ?? [];
    return new Set(raw.length === 0 ? universe : raw);
  }

  function openFilterMenu(colKey: string, universe: string[]) {
    setOpenFilterKey(colKey);
    setFilterSearchByColumn((p) => ({ ...p, [colKey]: "" }));
    setAddToListByColumn((p) => ({ ...p, [colKey]: false }));
    setDraftValuesByColumn((p) => ({ ...p, [colKey]: Array.from(currentSelectionSet(colKey, universe)) }));
  }

  function toggleDraftValue(colKey: string, value: string, universe: string[]) {
    setDraftValuesByColumn((prev) => {
      const base = new Set(prev[colKey] ?? Array.from(currentSelectionSet(colKey, universe)));
      if (base.has(value)) base.delete(value); else base.add(value);
      return { ...prev, [colKey]: Array.from(base) };
    });
  }

  function toggleDraftVisible(colKey: string, visible: string[], checked: boolean, universe: string[]) {
    setDraftValuesByColumn((prev) => {
      const base = new Set(prev[colKey] ?? Array.from(currentSelectionSet(colKey, universe)));
      for (const v of visible) { if (checked) base.add(v); else base.delete(v); }
      return { ...prev, [colKey]: Array.from(base) };
    });
  }

  function commitFilter(colKey: string, universe: string[], visibleValues: string[]) {
    const draft = new Set(draftValuesByColumn[colKey] ?? Array.from(currentSelectionSet(colKey, universe)));
    const hasSearch = (filterSearchByColumn[colKey] ?? "").trim().length > 0;
    const addToList = addToListByColumn[colKey] === true && hasSearch;
    const finalSelection = (() => {
      if (!addToList) return draft;
      const base = currentSelectionSet(colKey, universe);
      for (const v of visibleValues) { if (draft.has(v)) base.add(v); else base.delete(v); }
      return base;
    })();
    const next = Array.from(finalSelection);
    setSelectedValues((p) => ({ ...p, [colKey]: next.length === universe.length ? [] : next }));
    setOpenFilterKey(null);
  }

  useEffect(() => {
    if (!onRowSelect || spec.rowSelection === "none") return;
    const selectedKeys = Object.keys(rowSelection).filter((k) => rowSelection[k]);
    if (selectedKeys.length === 0) {
      if (skipNextEmptyRowSyncRef.current) {
        skipNextEmptyRowSyncRef.current = false;
        return;
      }
      onRowSelect({ type: "sync", selectedRows: [] });
      return;
    }
    skipNextEmptyRowSyncRef.current = false;
    const selectedRows: Record<string, unknown>[] = [];
    for (const key of selectedKeys) {
      const idx = Number(key);
      if (!Number.isFinite(idx) || idx < 0 || idx >= filteredRows.length) continue;
      const rowArr = filteredRows[idx];
      const rec: Record<string, unknown> = {};
      for (const col of model.columns) {
        const ci = columnIndexByKey.get(col.key);
        if (ci != null) rec[col.key] = rowArr[ci];
      }
      selectedRows.push(rec);
    }
    onRowSelect({ type: "sync", selectedRows });
  }, [rowSelection, filteredRows, model.columns, columnIndexByKey, onRowSelect, spec]);

  useEffect(() => {
    if (!openFilterKey) {
      setFilterPosition(null);
      return;
    }
    const anchor = headerCellRefs.current[openFilterKey];
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const menuWidth = 220;
    const gutter = 8;

    const wouldOverflowRight = rect.left + menuWidth > window.innerWidth - gutter;
    const left = wouldOverflowRight
      ? Math.max(gutter, rect.right - menuWidth)
      : rect.left;

    setFilterPosition({ top: rect.bottom + 4, left, ready: false });
  }, [openFilterKey]);

  useLayoutEffect(() => {
    if (!openFilterKey || !filterPosition || filterPosition.ready) return;
    const menu = filterMenuRef.current;
    const anchor = headerCellRefs.current[openFilterKey];
    if (!menu || !anchor) return;
    const rect = anchor.getBoundingClientRect();
    const menuHeight = menu.offsetHeight;
    const gutter = 8;
    const spaceBelow = window.innerHeight - rect.bottom;
    const top = spaceBelow >= menuHeight + gutter
      ? rect.bottom + 4
      : Math.max(gutter, rect.top - menuHeight - 4);
    setFilterPosition((prev) => (prev ? { ...prev, top, ready: true } : prev));
  }, [openFilterKey, filterPosition]);

  const distinctValuesByKey = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const col of model.columns) {
      const idx = columnIndexByKey.get(col.key);
      if (idx == null) continue;
      map.set(col.key, Array.from(new Set(model.rows.map((r) => String(r[idx] ?? "")))).sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: "accent" }),
      ));
    }
    return map;
  }, [model.columns, model.rows, columnIndexByKey]);

  const table = useReactTable({
    data: tableData,
    columns: columnDefs,
    state: {
      sorting,
      expanded,
      grouping,
      rowSelection,
      columnOrder: columnOrder.length > 0 ? columnOrder : undefined,
      columnPinning: initialPinning,
      columnSizing,
    },
    onSortingChange: setSorting,
    onExpandedChange: setExpanded,
    onRowSelectionChange: setRowSelection,
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: setColumnSizing,
    getCoreRowModel: getCoreRowModel(),
    getGroupedRowModel: grouping.length > 0 ? getGroupedRowModel() : undefined,
    getExpandedRowModel: grouping.length > 0 ? getExpandedRowModel() : undefined,
    getSortedRowModel: getSortedRowModel(),
    enableRowSelection: spec.rowSelection === "single" || spec.rowSelection === "multi",
    enableMultiRowSelection: spec.rowSelection === "multi",
    enableGrouping: grouping.length > 0,
    enableSorting: true,
    columnResizeMode: "onChange",
    getRowId,
  });

  const { rows: tableRows } = table.getRowModel();
  const parentRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: tableRows.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => (fitContent ? 44 : 34),
    overscan: 15,
    measureElement: fitContent ? virtualMeasureElement : undefined,
    getItemKey: (index) => tableRows[index]?.id ?? index,
  });

  useLayoutEffect(() => {
    rowVirtualizer.measure();
  }, [fitContent, columnSizing, tableRows.length, expanded, rowVirtualizer]);

  const virtualRows = rowVirtualizer.getVirtualItems();
  const paddingTop = virtualRows.length > 0 ? virtualRows[0].start : 0;
  const paddingBottom = virtualRows.length > 0
    ? rowVirtualizer.getTotalSize() - virtualRows[virtualRows.length - 1].end
    : 0;

  const placement = spec.totals?.placement ?? "footer";
  const showHeaderGrandTotal = grandTotalRow && !isolateSelection && (placement === "header" || placement === "both");
  const showFooterGrandTotal = grandTotalRow && !isolateSelection && (placement === "footer" || placement === "both");

  const headerGroups = table.getHeaderGroups();
  const leafColumns = table.getVisibleLeafColumns();
  const colCount = leafColumns.length + (spec.rowSelection === "multi" ? 1 : 0) + (showRowNumbers ? 1 : 0);

  const rowNumberByIndex = useMemo(() => {
    if (!showRowNumbers) return null;
    const out: Array<number | null> = new Array(tableRows.length);
    let n = 0;
    for (let i = 0; i < tableRows.length; i++) {
      if (tableRows[i].getIsGrouped()) {
        out[i] = null;
      } else {
        n++;
        out[i] = n;
      }
    }
    return out;
  }, [showRowNumbers, tableRows]);
  const hasAnyRowSelected = Object.keys(rowSelection).some((k) => rowSelection[k]);
  const showRowFilterTools = Boolean(onRowSelect) && (spec.rowSelection === "single" || spec.rowSelection === "multi");

  const theadRef = useRef<HTMLTableSectionElement>(null);
  const [headerRowOffsets, setHeaderRowOffsets] = useState<number[]>([]);

  useEffect(() => {
    const thead = theadRef.current;
    if (!thead) return;
    const computeOffsets = () => {
      const rows = thead.querySelectorAll("tr");
      let cumulative = 0;
      const offsets: number[] = [];
      rows.forEach((row) => {
        offsets.push(cumulative);
        cumulative += row.getBoundingClientRect().height;
      });
      setHeaderRowOffsets((prev) =>
        prev.length === offsets.length && prev.every((v, i) => v === offsets[i]) ? prev : offsets,
      );
    };
    computeOffsets();
    const observer = new ResizeObserver(computeOffsets);
    observer.observe(thead);
    return () => observer.disconnect();
  }, [headerGroups.length]);

  return (
    <div className={`tanTableWrap${fitContent ? " tanTableWrapFitContent" : ""}`}>
      <div className="tanTableMeta">
        <span>
          {isolateSelection
            ? `${tableData.length.toLocaleString()} shown${selectedSourceIndices.length > 0 ? ` · ${selectedSourceIndices.length} selected` : ""} (of ${filteredRows.length.toLocaleString()})`
            : `${filteredRows.length.toLocaleString()}${hasActiveFilters ? ` of ${model.rowCount.toLocaleString()}` : ""}`}{" "}
          row{(isolateSelection ? tableData.length : filteredRows.length) !== 1 ? "s" : ""}
        </span>
        <span className="tanTableGroupControls">
          <button
            type="button"
            className={`tanTableGroupDepthBtn${fitContent ? " isActive" : ""}`}
            title="When on, rows grow to show wrapped text; resize columns to reflow. When off, single-line cells with ellipsis."
            onClick={() => setFitContent((v) => !v)}
          >
            Fit content
          </button>
          {hasFoldControl && (
            <>
              <span className="tanTableGroupControlSep" />
              <span className="tanTableGroupControlLabel">Fold:</span>
              {foldFields.map((f) => {
                const isActive = activeFoldFields.has(f.field);
                return (
                  <button
                    key={f.field}
                    type="button"
                    className={`tanTableGroupDepthBtn ${isActive ? "isActive" : ""}`}
                    onClick={() => {
                      setActiveFoldFields((prev) => {
                        const next = new Set(prev);
                        if (next.has(f.field)) next.delete(f.field); else next.add(f.field);
                        return next;
                      });
                      setExpanded(true);
                    }}
                  >
                    {f.label}
                  </button>
                );
              })}
              {grouping.length > 0 && (
                <>
                  <span className="tanTableGroupControlSep" />
                  <button type="button" className="tanTableGroupDepthBtn" onClick={() => setExpanded(true)}>
                    Expand all
                  </button>
                  <button type="button" className="tanTableGroupDepthBtn" onClick={() => setExpanded({})}>
                    Collapse all
                  </button>
                </>
              )}
            </>
          )}
        </span>
        <span className="tanTableMetaSpacer" />
        {showRowFilterTools ? (
          <>
            <span className="tanTableGroupControlSep" />
            <button
              type="button"
              className="tanTableRowSelectBtn"
              disabled={!hasAnyRowSelected}
              title="Clear all row selection"
              onClick={() => {
                setRowSelection({});
                onRowSelect?.({ type: "sync", selectedRows: [] });
              }}
            >
              Clear selection
            </button>
            {spec.rowSelection === "multi" && onRowFilterUnselectedChange ? (
              <button
                type="button"
                className={`tanTableRowSelectBtn${rowFilterUnselected ? " tanTableRowSelectBtnActive" : ""}`}
                title="Drive detail widgets from selected rows vs rows you have not selected"
                onClick={() => onRowFilterUnselectedChange(!rowFilterUnselected)}
              >
                {rowFilterUnselected ? "Match: unselected rows" : "Match: selected rows"}
              </button>
            ) : null}
            {onIsolateSelectionChange ? (
              <button
                type="button"
                className={`tanTableRowSelectBtn${isolateSelection ? " tanTableRowSelectBtnActive" : ""}`}
                disabled={!isolateSelection && !hasAnyRowSelected}
                title="Narrow this table to only the selected row(s). Detail widgets still use the full dataset for filter logic."
                onClick={() => onIsolateSelectionChange(!isolateSelection)}
              >
                {isolateSelection ? "Show: all rows" : "Show: selected only"}
              </button>
            ) : null}
          </>
        ) : null}
        {showExport ? (
          <>
            <button
              type="button"
              className="tanTableExportBtn"
              disabled={
                copying ||
                model.rows.length === 0 ||
                model.rows.length > COPY_HARD_LIMIT_ROWS
              }
              title={
                model.rows.length > COPY_HARD_LIMIT_ROWS
                  ? `Too large to copy (${model.rows.length.toLocaleString()} rows). Use Export.`
                  : "Copy the table to your clipboard so you can paste it into Excel."
              }
              onClick={() => void handleCopy()}
            >
              {copying ? "Copying…" : "Copy table"}
            </button>
            <button
              type="button"
              className="tanTableExportBtn"
              disabled={exporting || model.rows.length === 0}
              onClick={handleExport}
            >
              {exporting ? "Exporting…" : "Export"}
            </button>
          </>
        ) : null}
      </div>
      <div className="tanTableScroller" ref={parentRef}>
        <table className="tanTableGrid" role="grid" style={{ width: table.getCenterTotalSize() }}>
          <thead ref={theadRef}>
            {headerGroups.map((headerGroup, rowIdx) => (
              <tr key={headerGroup.id}>
                {spec.rowSelection === "multi" && (
                  <th
                    className="tanTableCheckboxHeader"
                    style={{ width: 36, top: headerRowOffsets[rowIdx] ?? 0 }}
                  />
                )}
                {showRowNumbers && (
                  <th
                    className="tanTableRowNumberHeader"
                    style={{ top: headerRowOffsets[rowIdx] ?? 0 }}
                    aria-label="Row number"
                  />
                )}
                {headerGroup.headers.map((header) => (
                  <TanTableHeaderCell
                    key={header.id}
                    header={header}
                    stickyTop={headerRowOffsets[rowIdx] ?? 0}
                    openFilterKey={openFilterKey}
                    selectedValues={selectedValues}
                    filterSearchByColumn={filterSearchByColumn}
                    draftValuesByColumn={draftValuesByColumn}
                    addToListByColumn={addToListByColumn}
                    filterPosition={filterPosition}
                    distinctValuesByKey={distinctValuesByKey}
                    headerCellRefs={headerCellRefs}
                    filterMenuRef={filterMenuRef}
                    currentSelectionSet={currentSelectionSet}
                    openFilterMenu={openFilterMenu}
                    setOpenFilterKey={setOpenFilterKey}
                    setFilterSearchByColumn={setFilterSearchByColumn}
                    setDraftValuesByColumn={setDraftValuesByColumn}
                    setAddToListByColumn={setAddToListByColumn}
                    toggleDraftValue={toggleDraftValue}
                    toggleDraftVisible={toggleDraftVisible}
                    commitFilter={commitFilter}
                  />
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {showHeaderGrandTotal && (
              <GrandTotalRow
                columns={leafColumns}
                values={grandTotalRow!}
                model={model}
                formatters={columnFormatters}
                cellFitStyle={fitContent ? fitCellInnerStyle : undefined}
                showRowNumbers={showRowNumbers}
              />
            )}
            {paddingTop > 0 && (
              <tr><td colSpan={colCount} style={{ height: paddingTop, padding: 0, border: "none" }} /></tr>
            )}
            {virtualRows.map((virtualRow) => {
              const row = tableRows[virtualRow.index];
              const isGrouped = row.getIsGrouped();
              const isSelected = row.getIsSelected();

              return (
                <tr
                  key={row.id}
                  data-index={virtualRow.index}
                  ref={fitContent ? rowVirtualizer.measureElement : undefined}
                  className={
                    isGrouped
                      ? "tanTableGroupRow"
                      : isSelected
                        ? "tanTableSelected"
                        : undefined
                  }
                  onClick={
                    spec.rowSelection && spec.rowSelection !== "none" && !isGrouped
                      ? () => {
                          row.toggleSelected();
                        }
                      : undefined
                  }
                >
                  {spec.rowSelection === "multi" && (
                    <td
                      className="tanTableCheckboxCell"
                      style={{ width: 36, textAlign: "center" }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {!isGrouped && (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={row.getToggleSelectedHandler()}
                        />
                      )}
                    </td>
                  )}
                  {showRowNumbers && (
                    <td className="tanTableRowNumberCell">
                      {rowNumberByIndex?.[virtualRow.index] ?? ""}
                    </td>
                  )}
                  {row.getVisibleCells().map((cell, cellIdx) => {
                    const isGroupColumn = cell.getIsGrouped();
                    const isAggregated = cell.getIsAggregated();
                    const isPlaceholder = cell.getIsPlaceholder();
                    const meta = cell.column.columnDef.meta as TanTableColumnMeta | undefined;

                    const rawValue = cell.getValue();
                    const cfResult = !isGroupColumn && !isPlaceholder && meta?.conditionalFormat
                      ? evaluateConditionalFormat(meta.conditionalFormat, rawValue)
                      : null;

                    let suppress = false;
                    if (meta?.suppressDuplicates && virtualRow.index > 0 && !isGroupColumn) {
                      const prevRow = tableRows[virtualRow.index - 1];
                      if (prevRow && !prevRow.getIsGrouped()) {
                        const prevCell = prevRow.getVisibleCells()[cellIdx];
                        if (prevCell && prevCell.getValue() === rawValue) suppress = true;
                      }
                    }

                    const tdStyle: React.CSSProperties = { width: cell.column.getSize() };
                    if (fitContent) {
                      // Inline styles beat all CSS rules — the only reliable way to override
                      // the inherited white-space:nowrap from .tanTableGrid td.
                      tdStyle.whiteSpace = "normal";
                      tdStyle.wordBreak = "normal";
                      tdStyle.overflowWrap = "break-word";
                      tdStyle.overflow = "hidden";
                      tdStyle.verticalAlign = "top";
                    }
                    if (meta?.align) tdStyle.textAlign = meta.align;
                    if (cfResult?.bgColor) tdStyle.backgroundColor = cfResult.bgColor;
                    if (cfResult?.textColor) tdStyle.color = cfResult.textColor;

                    const groupLabel = isGroupColumn
                      ? renderColumnCell(cell.column.columnDef.cell, cell.getContext())
                      : null;
                    const dataCellContent =
                      !isGroupColumn && !isPlaceholder && !suppress
                        ? renderColumnCell(
                            isAggregated ? cell.column.columnDef.aggregatedCell : cell.column.columnDef.cell,
                            cell.getContext()
                          )
                        : null;

                    const rendered = isGroupColumn ? (
                      <button
                        className="tanTableGroupToggle"
                        onClick={(e) => { e.stopPropagation(); row.getToggleExpandedHandler()(); }}
                      >
                        <span className="tanTableGroupCaret">
                          {row.getIsExpanded() ? "▾" : "▸"}
                        </span>
                        <span className="tanTableGroupLabel">{groupLabel}</span>
                        <span className="tanTableGroupCount">
                          &nbsp;({row.subRows.length})
                        </span>
                      </button>
                    ) : dataCellContent;

                    return (
                      <td
                        key={cell.id}
                        style={tdStyle}
                        className={isAggregated ? "tanTableAggCell" : undefined}
                      >
                        {isGroupColumn ? (
                          rendered
                        ) : isPlaceholder || suppress ? null : (
                          <div
                            className="tanTableCellFitWrap"
                            style={fitContent ? fitCellInnerStyle : undefined}
                          >
                            {cfResult?.icon ? <span className="tanTableCondIcon">{cfResult.icon} </span> : null}
                            {rendered}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
            {paddingBottom > 0 && (
              <tr><td colSpan={colCount} style={{ height: paddingBottom, padding: 0, border: "none" }} /></tr>
            )}
            {showFooterGrandTotal && (
              <GrandTotalRow
                columns={leafColumns}
                values={grandTotalRow!}
                model={model}
                formatters={columnFormatters}
                cellFitStyle={fitContent ? fitCellInnerStyle : undefined}
              />
            )}
          </tbody>
        </table>
        {tableRows.length === 0 && (
          <div className="tanTableEmpty">
            No rows{hasActiveFilters ? " (filters may be hiding data)" : ""}
          </div>
        )}
        {isTruncated ? (
          <div className="muted" style={{ padding: "6px 8px" }}>
            Showing the first {model.rows.length.toLocaleString()}
            {model.render_truncated?.originalRowCount != null
              ? ` of ${model.render_truncated.originalRowCount.toLocaleString()}`
              : ""} rows. Export to get all data.
          </div>
        ) : null}
      </div>
    </div>
  );
}

type FilterProps = {
  openFilterKey: string | null;
  selectedValues: Record<string, string[]>;
  filterSearchByColumn: Record<string, string>;
  draftValuesByColumn: Record<string, string[]>;
  addToListByColumn: Record<string, boolean>;
  filterPosition: { top: number; left: number; ready: boolean } | null;
  distinctValuesByKey: Map<string, string[]>;
  headerCellRefs: React.MutableRefObject<Record<string, HTMLDivElement | null>>;
  filterMenuRef: React.MutableRefObject<HTMLDivElement | null>;
  currentSelectionSet: (colKey: string, universe: string[]) => Set<string>;
  openFilterMenu: (colKey: string, universe: string[]) => void;
  setOpenFilterKey: (key: string | null) => void;
  setFilterSearchByColumn: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  setDraftValuesByColumn: React.Dispatch<React.SetStateAction<Record<string, string[]>>>;
  setAddToListByColumn: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
  toggleDraftValue: (colKey: string, value: string, universe: string[]) => void;
  toggleDraftVisible: (colKey: string, visible: string[], checked: boolean, universe: string[]) => void;
  commitFilter: (colKey: string, universe: string[], visibleValues: string[]) => void;
};

function TanTableHeaderCell({
  header,
  stickyTop,
  openFilterKey,
  selectedValues,
  filterSearchByColumn,
  draftValuesByColumn,
  addToListByColumn,
  filterPosition,
  distinctValuesByKey,
  headerCellRefs,
  filterMenuRef,
  currentSelectionSet,
  openFilterMenu,
  setOpenFilterKey,
  setFilterSearchByColumn,
  setDraftValuesByColumn,
  setAddToListByColumn,
  toggleDraftValue,
  toggleDraftVisible,
  commitFilter,
}: { header: Header<RowArray, unknown>; stickyTop: number } & FilterProps) {
  const canSort = header.column.getCanSort();
  const sorted = header.column.getIsSorted();
  const isResizing = header.column.getIsResizing();

  const handleSort = useCallback(() => {
    if (canSort) header.column.toggleSorting();
  }, [canSort, header.column]);

  if (header.isPlaceholder) {
    return (
      <th
        colSpan={header.colSpan}
        className="tanTableGroupHeader"
        style={{ width: header.getSize(), top: stickyTop }}
      />
    );
  }

  const colKey = header.column.id;
  const isLeaf = !header.subHeaders?.length;
  const distinctValues = distinctValuesByKey.get(colKey) ?? [];
  const activeFilter = selectedValues[colKey] ?? [];
  const search = filterSearchByColumn[colKey] ?? "";
  const visibleValues = distinctValues.filter((v) => v.toLowerCase().includes(search.toLowerCase()));
  const draftSelection = new Set(draftValuesByColumn[colKey] ?? Array.from(currentSelectionSet(colKey, distinctValues)));
  const allVisibleChecked = visibleValues.length > 0 && visibleValues.every((v) => draftSelection.has(v));
  const someVisibleChecked = visibleValues.some((v) => draftSelection.has(v));
  const showAddToList = search.trim().length > 0;
  const isFilterOpen = openFilterKey === colKey;

  if (!isLeaf) {
    return (
      <th
        colSpan={header.colSpan}
        className="tanTableGroupHeader"
        style={{ width: header.getSize(), top: stickyTop }}
      >
        {flexRender(header.column.columnDef.header, header.getContext())}
      </th>
    );
  }

  return (
    <th colSpan={header.colSpan} style={{ width: header.getSize(), top: stickyTop }}>
      <div
        className="tanTableHeaderCell"
        ref={(node) => { headerCellRefs.current[colKey] = node; }}
      >
        <span
          className="tanTableHeaderLabel"
          onClick={handleSort}
          style={{ cursor: canSort ? "pointer" : "default" }}
        >
          {flexRender(header.column.columnDef.header, header.getContext())}
        </span>
        {sorted && (
          <span className="tanTableSortIcon" onClick={handleSort} style={{ cursor: "pointer" }}>
            {sorted === "asc" ? "↑" : "↓"}
          </span>
        )}
        {isLeaf && distinctValues.length > 0 && (
          <button
            type="button"
            className={`tanTableHeaderBtn ${activeFilter.length > 0 ? "isActive" : ""}`}
            title="Filter"
            onClick={() => {
              if (isFilterOpen) { setOpenFilterKey(null); return; }
              openFilterMenu(colKey, distinctValues);
            }}
          >
            ⛃
          </button>
        )}
        {isFilterOpen && filterPosition && createPortal(
          <div
            ref={filterMenuRef}
            className="tableVizFilterMenu isFixed"
            style={{
              top: filterPosition.top,
              left: filterPosition.left,
              visibility: filterPosition.ready ? "visible" : "hidden",
            }}
          >
            <div className="tableVizFilterActions">
              <input
                type="text"
                className="tableVizFilterSearch"
                placeholder="Search values..."
                value={search}
                onChange={(e) => setFilterSearchByColumn((p) => ({ ...p, [colKey]: e.target.value }))}
              />
            </div>
            <div className="tableVizFilterActions tableVizFilterActionsTight">
              <label className="tableVizFilterSelectAll">
                <input
                  type="checkbox"
                  checked={allVisibleChecked}
                  ref={(node) => { if (node) node.indeterminate = !allVisibleChecked && someVisibleChecked; }}
                  onChange={(e) => toggleDraftVisible(colKey, visibleValues, e.target.checked, distinctValues)}
                />
                <span>Select all</span>
              </label>
            </div>
            {showAddToList && (
              <div className="tableVizFilterActions tableVizFilterActionsTight">
                <label className="tableVizFilterSelectAll">
                  <input
                    type="checkbox"
                    checked={addToListByColumn[colKey] === true}
                    onChange={(e) => setAddToListByColumn((p) => ({ ...p, [colKey]: e.target.checked }))}
                  />
                  <span>Add to current list</span>
                </label>
              </div>
            )}
            <div className="tableVizFilterActions">
              <button type="button" className="tableVizFilterActionBtn"
                onClick={() => setDraftValuesByColumn((p) => ({ ...p, [colKey]: distinctValues }))}
              >All</button>
              <button type="button" className="tableVizFilterActionBtn"
                onClick={() => setDraftValuesByColumn((p) => ({ ...p, [colKey]: [] }))}
              >None</button>
              <button type="button" className="tableVizFilterActionBtn"
                onClick={() => commitFilter(colKey, distinctValues, visibleValues)}
              >Apply</button>
              <button type="button" className="tableVizFilterActionBtn"
                onClick={() => setOpenFilterKey(null)}
              >Close</button>
            </div>
            <div className="tableVizFilterList">
              {visibleValues.map((value) => (
                <label key={`${colKey}_${value}`} className="tableVizFilterItem">
                  <input type="checkbox" checked={draftSelection.has(value)} onChange={() => toggleDraftValue(colKey, value, distinctValues)} />
                  <span>{value || "(blank)"}</span>
                </label>
              ))}
              {visibleValues.length === 0 && (
                <span className="tableVizFilterEmpty">No matching values</span>
              )}
            </div>
          </div>,
          document.body,
        )}
      </div>
      <div
        className={`tanTableResizeHandle ${isResizing ? "isResizing" : ""}`}
        onMouseDown={header.getResizeHandler()}
        onTouchStart={header.getResizeHandler()}
      />
    </th>
  );
}

function GrandTotalRow({
  columns,
  values,
  model,
  formatters,
  cellFitStyle,
  showRowNumbers = false,
}: {
  columns: ReturnType<ReturnType<typeof useReactTable<RowArray>>["getVisibleLeafColumns"]>;
  values: unknown[];
  model: TanTableRenderModel;
  formatters: Array<(value: unknown) => string>;
  cellFitStyle?: CSSProperties;
  showRowNumbers?: boolean;
}) {
  return (
    <tr className="tanTableGrandTotalRow">
      {model.tableSpec.rowSelection === "multi" && <td className="tanTableCheckboxCell" style={{ width: 36 }} />}
      {showRowNumbers && <td className="tanTableRowNumberCell" />}
      {columns.map((col, ci) => {
        const raw = values[ci];
        const display = ci === 0 && raw == null
          ? "Grand Total"
          : formatters[ci] ? formatters[ci](raw) : String(raw ?? "");
        return (
          <td key={col.id} style={{ width: col.getSize() }}>
            <div className="tanTableCellFitWrap" style={cellFitStyle}>
              {display}
            </div>
          </td>
        );
      })}
    </tr>
  );
}
